import { useCallback, useEffect, useRef, useState } from "react";

import { useAuthContext } from "../auth/AuthContext";
import { PpLoginGate } from "../auth/ppLoginGate";
import {
  ensurePpAccessToken,
  getPpToken,
  hasPersistedPpLink,
  resolveIdentityEmail,
} from "../auth/ppToken";

/**
 * Practice Schedule access gate — a deliberately TRIMMED sibling of
 * `useWorklistAccessGate` (worklist/useWorklistAccessGate.ts).
 *
 * The schedule's `practiceSchedule` resolver is PP-scoped: care-management-gql
 * scopes the providers/appointments it returns to the NPIs the signed-in
 * Provider Portal identity can see, so the ONLY precondition here is a live PP
 * bearer. Unlike the worklist, the schedule needs NO provider-entered NPI and
 * NO IPA (companyId) resolution — the backend derives the accessible-provider
 * set from the bearer itself. So this gate reuses ONLY the worklist gate's
 * PP-SESSION half:
 *
 *   - `getPpToken` — the live, identity-verified PP bearer (or undefined).
 *   - `hasPersistedPpLink` / `ensurePpAccessToken` — the ACT-1247 silent-first
 *     refresh: if this identity has already linked PP but holds no LIVE bearer
 *     yet, try a silent refresh before ever prompting for credentials.
 *   - `resolveIdentityEmail(accountLabel)` + `useAuthContext()` — identity.
 *   - `PpLoginGate` / `PpLoginModal` — the interactive credential prompt.
 *
 * The NPI (npiStorage) and IPA (ipaStorage / fetchEntitledIpas) machinery the
 * worklist gate carries is intentionally absent.
 */
export type ScheduleAccessStatus = "no-pp-session" | "resolving" | "ready";

/**
 * Pure status derivation — unit-testable without mounting a React tree.
 *
 * - A live PP bearer is all the schedule needs → `ready`.
 * - No bearer yet, but this identity has a persisted PP link whose silent
 *   refresh hasn't SETTLED for the current identity → `resolving` (a brief
 *   spinner rather than flashing the login CTA while the refresh is in
 *   flight — or before the kicking effect has even run).
 * - Otherwise (no link, or the silent refresh settled without producing a
 *   token) → `no-pp-session`, the interactive-login CTA.
 */
export function computeScheduleAccessStatus(
  hasPpBearer: boolean,
  hasPersistedLink: boolean,
  refreshSettled: boolean,
): ScheduleAccessStatus {
  if (hasPpBearer) return "ready";
  if (hasPersistedLink && !refreshSettled) return "resolving";
  return "no-pp-session";
}

export interface UseScheduleAccessGateResult {
  status: ScheduleAccessStatus;
  /** The live PP bearer to forward to GraphQLProvider — set iff status "ready". */
  ppBearer: string | undefined;
  /** Email of whoever is currently signed in, or undefined if unresolvable. */
  identityEmail: string | undefined;
  /** Opens the interactive PP login modal (idempotent — safe to call repeatedly). */
  onLogin: () => void;
  /** Drives the shared PpLoginModal — true while a login prompt is pending. */
  loginOpen: boolean;
  onLoginSuccess: () => void;
  onLoginCancel: () => void;
}

export function useScheduleAccessGate(): UseScheduleAccessGateResult {
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const identityEmail = resolveIdentityEmail(accountLabel);

  // Bump-only counter to force a re-render after a background mutation that
  // bypasses React state (a silent ensurePpAccessToken refresh, or the
  // PpLoginModal success handler) — getPpToken reads live localStorage on
  // every render, so bumping this is enough to re-derive the bearer.
  const [, setTick] = useState(0);

  // The identity for which the silent refresh has SETTLED (resolved either
  // way). Tracked by email — not a bare boolean — so a mid-session identity
  // switch to a DIFFERENT linked account correctly re-enters `resolving`
  // instead of inheriting the previous identity's settled flag.
  const [settledEmail, setSettledEmail] = useState<string | undefined>(undefined);

  const [loginOpen, setLoginOpen] = useState(false);
  const gateRef = useRef<PpLoginGate | null>(null);
  if (!gateRef.current) {
    gateRef.current = new PpLoginGate({ onOpen: () => setLoginOpen(true) });
  }

  const ppBearer = getPpToken(selectedProvider, accessToken, identityEmail);
  const hasPersistedLink = !!identityEmail && hasPersistedPpLink(identityEmail);

  // Mirrors MemberProfilePage's / the worklist gate's ensurePpSession
  // silent-first step (ACT-1247 SSO-PP auth-linking Phase 1, Option A): with
  // no LIVE bearer but a persisted link for this identity, try a silent
  // refresh before ever prompting for credentials. Marks the attempt SETTLED
  // (for the identity it ran on) either way, so a failed refresh drops out of
  // `resolving` into the login CTA rather than spinning forever.
  useEffect(() => {
    if (ppBearer) return;
    if (!identityEmail || !hasPersistedPpLink(identityEmail)) return;
    let cancelled = false;
    void ensurePpAccessToken(identityEmail).then((token) => {
      if (cancelled) return;
      setSettledEmail(identityEmail);
      if (token) setTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [identityEmail, ppBearer]);

  const onLogin = useCallback(() => {
    void gateRef.current?.request().catch(() => {
      /* cancelled — status simply stays "no-pp-session" */
    });
  }, []);

  const onLoginCancel = useCallback(() => {
    setLoginOpen(false);
    gateRef.current?.cancel();
  }, []);

  const onLoginSuccess = useCallback(() => {
    setLoginOpen(false);
    gateRef.current?.succeed();
    setTick((t) => t + 1);
  }, []);

  const refreshSettled = settledEmail !== undefined && settledEmail === identityEmail;
  const status = computeScheduleAccessStatus(!!ppBearer, hasPersistedLink, refreshSettled);

  return {
    status,
    ppBearer: status === "ready" ? ppBearer : undefined,
    identityEmail,
    onLogin,
    loginOpen,
    onLoginSuccess,
    onLoginCancel,
  };
}
